import React, { useMemo } from 'react';
import { Download, DollarSign, Clock, Users, AlertTriangle } from 'lucide-react';
import { StaffMetrics } from '../../types/ltss';
import { getStaffPayRates, PayType } from '../Settings/StaffPayRates';
import { formatCurrency } from '../../utils/timeUtils';

interface PayrollReportProps {
  staffMetrics: StaffMetrics[];
}

interface PayrollEntry {
  staffName: string;
  week1Hours: number;
  week2Hours: number;
  totalHours: number;
  payType: PayType;
  hourlyRate: number;
  salary: number;
  estimatedPay: number;
  hasRate: boolean;
}

export function PayrollReport({ staffMetrics }: PayrollReportProps) {
  const payrollData = useMemo(() => {
    const rates = getStaffPayRates();
    const rateMap: Record<string, { payType: PayType; hourlyRate: number; salary: number }> = {};
    rates.forEach(r => {
      rateMap[r.staffName.toLowerCase()] = {
        payType: r.payType || 'hourly',
        hourlyRate: r.hourlyRate || 0,
        salary: r.salary || 0,
      };
    });

    // Group staff weekly hours into bi-weekly periods
    const entries: PayrollEntry[] = staffMetrics.map(staff => {
      // Get the two most recent weeks
      const sortedWeeks = [...(staff.weeklyHours || [])].sort(
        (a, b) => b.weekStart.getTime() - a.weekStart.getTime()
      );

      const week1 = sortedWeeks[0];
      const week2 = sortedWeeks[1];

      const week1Hours = week1?.billableHours || 0;
      const week2Hours = week2?.billableHours || 0;
      const totalHours = week1Hours + week2Hours;

      const staffRate = rateMap[staff.staffName.toLowerCase()];
      const payType = staffRate?.payType || 'hourly';
      const hourlyRate = staffRate?.hourlyRate || 0;
      const salary = staffRate?.salary || 0;
      
      // Calculate estimated pay based on pay type
      let estimatedPay = 0;
      let hasRate = false;
      
      if (payType === 'salary') {
        estimatedPay = salary;
        hasRate = salary > 0;
      } else {
        estimatedPay = totalHours * hourlyRate;
        hasRate = hourlyRate > 0;
      }

      return {
        staffName: staff.staffName,
        week1Hours,
        week2Hours,
        totalHours,
        payType,
        hourlyRate,
        salary,
        estimatedPay,
        hasRate,
      };
    });

    // Sort by total hours descending
    return entries.sort((a, b) => b.totalHours - a.totalHours);
  }, [staffMetrics]);

  const totals = useMemo(() => {
    return payrollData.reduce(
      (acc, entry) => ({
        week1Hours: acc.week1Hours + entry.week1Hours,
        week2Hours: acc.week2Hours + entry.week2Hours,
        totalHours: acc.totalHours + entry.totalHours,
        estimatedPay: acc.estimatedPay + entry.estimatedPay,
      }),
      { week1Hours: 0, week2Hours: 0, totalHours: 0, estimatedPay: 0 }
    );
  }, [payrollData]);

  const staffWithoutRates = payrollData.filter(e => !e.hasRate && e.totalHours > 0);

  const handleExportCSV = () => {
    const headers = ['Staff Name', 'Week 1 Hours', 'Week 2 Hours', 'Total Hours', 'Pay Type', 'Hourly Rate', 'Bi-Weekly Salary', 'Estimated Pay'];
    const rows = payrollData.map(e => [
      e.staffName,
      e.week1Hours.toFixed(2),
      e.week2Hours.toFixed(2),
      e.totalHours.toFixed(2),
      e.payType === 'salary' ? 'Salary' : 'Hourly',
      e.payType === 'hourly' ? e.hourlyRate.toFixed(2) : '',
      e.payType === 'salary' ? e.salary.toFixed(2) : '',
      e.estimatedPay.toFixed(2),
    ]);

    // Add totals row
    rows.push([
      'TOTALS',
      totals.week1Hours.toFixed(2),
      totals.week2Hours.toFixed(2),
      totals.totalHours.toFixed(2),
      '',
      '',
      '',
      totals.estimatedPay.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payroll_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-slate-200">Bi-Weekly Payroll Estimate</h3>
          <p className="text-sm text-slate-400 mt-1">
            Based on billable hours from uploaded EVV report
          </p>
        </div>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Staff</p>
              <p className="text-xl font-bold text-blue-400 mt-1">{payrollData.length}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Hours</p>
              <p className="text-xl font-bold text-purple-400 mt-1">{totals.totalHours.toFixed(1)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Est. Payroll</p>
              <p className="text-xl font-bold text-green-400 mt-1">{formatCurrency(totals.estimatedPay)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Missing Rates</p>
              <p className={`text-xl font-bold mt-1 ${staffWithoutRates.length > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                {staffWithoutRates.length}
              </p>
            </div>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${staffWithoutRates.length > 0 ? 'bg-orange-500/10' : 'bg-green-500/10'}`}>
              <AlertTriangle className={`w-5 h-5 ${staffWithoutRates.length > 0 ? 'text-orange-400' : 'text-green-400'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Warning for missing rates */}
      {staffWithoutRates.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-400">
                {staffWithoutRates.length} staff member(s) missing pay rates
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Go to Settings → Staff Pay Rates to configure hourly rates for accurate payroll estimation.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {staffWithoutRates.slice(0, 5).map(e => (
                  <span key={e.staffName} className="px-2 py-1 text-xs bg-slate-700 text-slate-300 rounded">
                    {e.staffName}
                  </span>
                ))}
                {staffWithoutRates.length > 5 && (
                  <span className="px-2 py-1 text-xs text-slate-500">
                    +{staffWithoutRates.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                  Staff Name
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">
                  Week 1
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">
                  Week 2
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">
                  Total Hrs
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">
                  Rate / Salary
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">
                  Est. Pay
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {payrollData.map((entry) => (
                <tr key={entry.staffName} className="hover:bg-slate-800/30">
                  <td className="px-4 py-3 text-sm text-slate-200 font-medium">
                    {entry.staffName}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300 text-right">
                    {entry.week1Hours.toFixed(1)} hrs
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300 text-right">
                    {entry.week2Hours.toFixed(1)} hrs
                  </td>
                  <td className="px-4 py-3 text-sm text-white font-medium text-right">
                    {entry.totalHours.toFixed(1)} hrs
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    <span className={`px-2 py-0.5 rounded text-xs ${entry.payType === 'salary' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                      {entry.payType === 'salary' ? 'Salary' : 'Hourly'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {entry.hasRate ? (
                      entry.payType === 'salary' ? (
                        <span className="text-blue-400">{formatCurrency(entry.salary)}/bi-wk</span>
                      ) : (
                        <span className="text-green-400">${entry.hourlyRate.toFixed(2)}/hr</span>
                      )
                    ) : (
                      <span className="text-orange-400">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-right">
                    {entry.hasRate ? (
                      <span className="text-green-400">{formatCurrency(entry.estimatedPay)}</span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-800/50 border-t-2 border-slate-600">
              <tr>
                <td className="px-4 py-3 text-sm font-bold text-slate-200">
                  TOTALS
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-200 text-right">
                  {totals.week1Hours.toFixed(1)} hrs
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-200 text-right">
                  {totals.week2Hours.toFixed(1)} hrs
                </td>
                <td className="px-4 py-3 text-sm font-bold text-white text-right">
                  {totals.totalHours.toFixed(1)} hrs
                </td>
                <td className="px-4 py-3 text-sm text-center">
                  —
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  —
                </td>
                <td className="px-4 py-3 text-sm font-bold text-green-400 text-right">
                  {formatCurrency(totals.estimatedPay)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {payrollData.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No staff data available. Upload an EVV report to see payroll estimates.
          </div>
        )}
      </div>
    </div>
  );
}
